import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { posthogOptInCapturing, posthogOptOut } from "@/lib/posthog";
import { ensureSentryInit } from "@/lib/sentry";
import { OPEN_COOKIE_SETTINGS_EVENT, readCookieConsent, saveCookieConsent } from "@/lib/cookieConsent";

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Pas de choix, ou choix de plus de 6 mois : on (re)pose la question.
    if (readCookieConsent() === null) setVisible(true);

    // « Gérer mes cookies » rouvre le bandeau pour changer d'avis.
    const open = () => setVisible(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, []);

  const handleAccept = () => {
    saveCookieConsent("accepted");
    setVisible(false);
    posthogOptInCapturing();
    // Ensure Sentry starts only after explicit opt-in.
    void ensureSentryInit();
  };

  const handleReject = () => {
    const withdrawsConsent = readCookieConsent() === "accepted";
    saveCookieConsent("rejected");
    setVisible(false);
    posthogOptOut();
    // Retrait d'un accord : Sentry et PostHog tournent peut-être déjà dans cette page,
    // seul un rechargement les arrête (ils ne redémarrent pas sans accord).
    if (withdrawsConsent) window.location.reload();
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-in slide-in-from-bottom-4 duration-base">
      <div className="mx-auto max-w-3xl rounded-xl border bg-card shadow-lg p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
        <p className="text-payfit-body text-muted-foreground flex-1">
          Ce site utilise des cookies et un stockage local indispensables à son fonctionnement. « Accepter » autorise aussi la supervision des erreurs (Sentry) et la mesure d'usage (PostHog), avec un enregistrement des sessions où les textes sont masqués. Votre choix est gardé 6 mois et modifiable à tout moment par le lien « Gérer mes cookies ». Aucun cookie publicitaire.{" "}
          <a href="/confidentialite#cookies" className="underline text-foreground hover:text-primary transition-colors">
            En savoir plus
          </a>
        </p>
        {/* Refuser aussi simple et visible qu'accepter (CNIL) : même style, même largeur, 44 px de haut. */}
        <div className="grid grid-cols-2 w-full sm:w-auto gap-2 shrink-0">
          <Button size="lg" className="px-6" onClick={handleAccept}>
            Accepter
          </Button>
          <Button size="lg" className="px-6" onClick={handleReject}>
            Refuser
          </Button>
        </div>
      </div>
    </div>
  );
};
